import React, { useEffect, useState, useRef } from 'react';
import { MailIcon, PhoneIcon, MapPinIcon, ClockIcon, CheckCircleIcon, ChevronDownIcon, LinkedinIcon, FacebookIcon, TwitterIcon, InstagramIcon, MessageSquareIcon, HeadphonesIcon, BuildingIcon, SendIcon } from 'lucide-react';
import { Header } from './Header';
import { Footer } from './Footer';
export const ContactPage = () => {
  return <div className="flex flex-col min-h-screen w-full bg-white text-charcoal" data-id="element-259">
      <style jsx global data-id="element-260">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        html {
          scroll-behavior: smooth;
        }
        body {
          font-family: 'Inter', sans-serif;
        }
        .gradient-text {
          background: linear-gradient(135deg, #0b757a 0%, #1ddbdb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .card-hover {
          transition: all 0.3s ease;
        }
        .card-hover:hover {
          transform: translateY(-8px);
        }
      `}</style>
      <Header data-id="element-261" />
      <main className="flex-grow" data-id="element-262">
        <HeroSection data-id="element-263" />
        <ContactOptionsSection data-id="element-264" />
        <ContactFormSection data-id="element-265" />
        <OfficeLocationsSection data-id="element-266" />
        <FaqSection data-id="element-267" />
      </main>
      <Footer data-id="element-268" />
    </div>;
};
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    setIsVisible(true);
  }, []);
  return <section className="relative py-24 md:py-32 bg-gradient-to-b from-charcoal to-charcoal/95 overflow-hidden" data-id="element-269">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0" data-id="element-270">
        <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Background" className="w-full h-full object-cover opacity-20" data-id="element-271" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/70 to-charcoal/90" data-id="element-272"></div>
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-soft-light" data-id="element-273"></div>
      </div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-274">
        <div className="max-w-4xl mx-auto text-center" data-id="element-275">
          <div className={`transition-all duration-700 delay-300 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-276">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6" data-id="element-277">
              <MessageSquareIcon className="h-4 w-4 text-aqua mr-2" data-id="element-278" />
              <span className="text-white/90 text-sm font-medium" data-id="element-279">
                We're here to help
              </span>
            </div>
          </div>
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white transition-all duration-700 delay-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-280">
            Get in <span className="gradient-text" data-id="element-281">Touch</span> With Us
          </h1>
          <p className={`text-xl text-white/80 mb-10 transition-all duration-700 delay-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-282">
            Have questions about our courses or need assistance with your
            training? Our team is ready to help you succeed.
          </p>
        </div>
      </div>
    </section>;
};
const ContactOptionsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const contactOptions = [{
    icon: <HeadphonesIcon className="h-8 w-8 text-white" data-id="element-283" />,
    title: 'Customer Support',
    description: 'Get help with your courses, account, or technical issues',
    cta: 'Contact Support',
    link: '#support-form',
    color: 'from-aqua to-teal'
  }, {
    icon: <BuildingIcon className="h-8 w-8 text-white" data-id="element-284" />,
    title: 'Enterprise Solutions',
    description: 'Learn more about our enterprise training programs',
    cta: 'Book a Demo',
    link: '#enterprise-form',
    color: 'from-teal to-teal/90'
  }, {
    icon: <MessageSquareIcon className="h-8 w-8 text-white" data-id="element-285" />,
    title: 'General Inquiries',
    description: 'For partnerships, media, or other business inquiries',
    cta: 'Send Message',
    link: '#general-form',
    color: 'from-charcoal to-charcoal/90'
  }];
  return <section ref={sectionRef} className="py-20 bg-white" data-id="element-286">
      <div className="container mx-auto px-4 md:px-6" data-id="element-287">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-288">
          {contactOptions.map((option, index) => <div key={index} className={`rounded-xl shadow-lg overflow-hidden transition-all duration-700 transform card-hover ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} style={{
          transitionDelay: `${index * 150}ms`
        }} data-id="element-289">
              <div className={`bg-gradient-to-br ${option.color} p-8`} data-id="element-290">
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20" data-id="element-291">
                  {option.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white" data-id="element-292">
                  {option.title}
                </h3>
                <p className="text-white/80 mb-6" data-id="element-293">{option.description}</p>
                <a href={option.link} className="inline-flex items-center text-white font-medium hover:translate-x-1 transition-transform" data-id="element-294">
                  {option.cta}
                  <SendIcon className="h-4 w-4 ml-2" data-id="element-295" />
                </a>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const ContactFormSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    reason: 'support'
  });
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    // For demo purposes, we'll just set formSubmitted to true
    setFormSubmitted(true);
    // Reset form after submission
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
      reason: 'support'
    });
  };
  return <section id="support-form" ref={sectionRef} className="py-20 bg-gray-50" data-id="element-296">
      <div className="container mx-auto px-4 md:px-6" data-id="element-297">
        <div className="max-w-6xl mx-auto" data-id="element-298">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-299">
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} data-id="element-300">
              <h2 className="text-4xl font-bold mb-6 text-charcoal" data-id="element-301">
                Send us a <span className="gradient-text" data-id="element-302">Message</span>
              </h2>
              <p className="text-xl text-charcoal/70 mb-8" data-id="element-303">
                Fill out the form and our team will get back to you within 24
                hours. We're committed to helping you with any questions or
                concerns.
              </p>
              <div className="space-y-6 mb-8" data-id="element-304">
                <div className="flex items-start" data-id="element-305">
                  <div className="flex-shrink-0 mt-1" data-id="element-306">
                    <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center" data-id="element-307">
                      <MailIcon className="h-5 w-5 text-teal" data-id="element-308" />
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-309">
                    <h3 className="text-lg font-semibold text-charcoal" data-id="element-310">
                      Email Us
                    </h3>
                    <p className="text-charcoal/70" data-id="element-311">support@kivarro.com</p>
                    <p className="text-charcoal/70" data-id="element-312">enterprise@kivarro.com</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-313">
                  <div className="flex-shrink-0 mt-1" data-id="element-314">
                    <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center" data-id="element-315">
                      <PhoneIcon className="h-5 w-5 text-teal" data-id="element-316" />
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-317">
                    <h3 className="text-lg font-semibold text-charcoal" data-id="element-318">
                      Call Us
                    </h3>
                    <p className="text-charcoal/70" data-id="element-319">+353 (0)1 555 1234</p>
                    <p className="text-charcoal/70" data-id="element-320">+44 (0)20 7946 0958</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-321">
                  <div className="flex-shrink-0 mt-1" data-id="element-322">
                    <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center" data-id="element-323">
                      <ClockIcon className="h-5 w-5 text-teal" data-id="element-324" />
                    </div>
                  </div>
                  <div className="ml-4" data-id="element-325">
                    <h3 className="text-lg font-semibold text-charcoal" data-id="element-326">
                      Hours
                    </h3>
                    <p className="text-charcoal/70" data-id="element-327">
                      Monday - Friday: 9:00 AM - 5:30 PM
                    </p>
                    <p className="text-charcoal/70" data-id="element-328">Weekend: Closed</p>
                  </div>
                </div>
              </div>
              <div className="flex space-x-4" data-id="element-329">
                <a href="https://linkedin.com" className="h-10 w-10 rounded-full bg-charcoal flex items-center justify-center text-white hover:bg-teal transition-colors duration-300" data-id="element-330">
                  <LinkedinIcon className="h-5 w-5" data-id="element-331" />
                </a>
                <a href="https://facebook.com" className="h-10 w-10 rounded-full bg-charcoal flex items-center justify-center text-white hover:bg-teal transition-colors duration-300" data-id="element-332">
                  <FacebookIcon className="h-5 w-5" data-id="element-333" />
                </a>
                <a href="https://twitter.com" className="h-10 w-10 rounded-full bg-charcoal flex items-center justify-center text-white hover:bg-teal transition-colors duration-300" data-id="element-334">
                  <TwitterIcon className="h-5 w-5" data-id="element-335" />
                </a>
                <a href="https://instagram.com" className="h-10 w-10 rounded-full bg-charcoal flex items-center justify-center text-white hover:bg-teal transition-colors duration-300" data-id="element-336">
                  <InstagramIcon className="h-5 w-5" data-id="element-337" />
                </a>
              </div>
            </div>
            <div className={`transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} style={{
            transitionDelay: '200ms'
          }} data-id="element-338">
              {formSubmitted ? <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100" data-id="element-339">
                  <div className="text-center" data-id="element-340">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal/10 mb-6" data-id="element-341">
                      <CheckCircleIcon className="h-8 w-8 text-teal" data-id="element-342" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-charcoal" data-id="element-343">
                      Message Sent!
                    </h3>
                    <p className="text-charcoal/70 mb-6" data-id="element-344">
                      Thank you for contacting us. We've received your message
                      and will get back to you within 24 hours.
                    </p>
                    <button onClick={() => setFormSubmitted(false)} className="px-6 py-3 bg-teal text-white font-medium rounded-lg hover:bg-teal/90 transition-colors" data-id="element-345">
                      Send Another Message
                    </button>
                  </div>
                </div> : <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100" data-id="element-346">
                  <form onSubmit={handleSubmit} data-id="element-347">
                    <div className="mb-6" data-id="element-348">
                      <label className="block text-charcoal font-medium mb-2" data-id="element-349">
                        I'm contacting about
                      </label>
                      <div className="relative" data-id="element-350">
                        <select name="reason" value={formData.reason} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" required data-id="element-351">
                          <option value="support" data-id="element-352">Customer Support</option>
                          <option value="enterprise" data-id="element-353">
                            Enterprise Solutions
                          </option>
                          <option value="general" data-id="element-354">General Inquiry</option>
                          <option value="feedback" data-id="element-355">Feedback</option>
                        </select>
                        <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-charcoal/60 h-5 w-5 pointer-events-none" data-id="element-356" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-357">
                      <div data-id="element-358">
                        <label className="block text-charcoal font-medium mb-2" data-id="element-359">
                          Full Name
                        </label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" placeholder="Your name" required data-id="element-360" />
                      </div>
                      <div data-id="element-361">
                        <label className="block text-charcoal font-medium mb-2" data-id="element-362">
                          Email Address
                        </label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" placeholder="your.email@example.com" required data-id="element-363" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-364">
                      <div data-id="element-365">
                        <label className="block text-charcoal font-medium mb-2" data-id="element-366">
                          Phone Number (Optional)
                        </label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" placeholder="+1 (555) 000-0000" data-id="element-367" />
                      </div>
                      <div data-id="element-368">
                        <label className="block text-charcoal font-medium mb-2" data-id="element-369">
                          Subject
                        </label>
                        <input type="text" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" placeholder="How can we help?" required data-id="element-370" />
                      </div>
                    </div>
                    <div className="mb-6" data-id="element-371">
                      <label className="block text-charcoal font-medium mb-2" data-id="element-372">
                        Message
                      </label>
                      <textarea name="message" value={formData.message} onChange={handleChange} rows="5" className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent" placeholder="Tell us how we can assist you..." required data-id="element-373"></textarea>
                    </div>
                    <button type="submit" className="w-full py-4 bg-teal text-white font-medium rounded-lg hover:bg-teal/90 transition-colors shadow-md" data-id="element-374">
                      Send Message
                    </button>
                  </form>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const OfficeLocationsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const offices = [{
    city: 'Dublin',
    country: 'Ireland',
    address: 'Grand Canal Dock, Dublin 2',
    image: 'https://images.unsplash.com/photo-1565769583756-b4070267ee33?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1171&q=80',
    primary: true
  }, {
    city: 'London',
    country: 'United Kingdom',
    address: 'Canary Wharf, London E14',
    image: 'https://images.unsplash.com/photo-1533929736458-ca588d08c8be?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    primary: false
  }, {
    city: 'Amsterdam',
    country: 'Netherlands',
    address: 'Zuidas, 1082 MD Amsterdam',
    image: 'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80',
    primary: false
  }];
  return <section ref={sectionRef} className="py-20 bg-white" data-id="element-375">
      <div className="container mx-auto px-4 md:px-6" data-id="element-376">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-377">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-378">
            <MapPinIcon className="h-6 w-6 text-teal" data-id="element-379" />
          </div>
          <h2 className="text-4xl font-bold mb-6 text-charcoal" data-id="element-380">
            Our <span className="gradient-text" data-id="element-381">Locations</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-382">
            Visit one of our offices across Europe or schedule a virtual meeting
            with our team.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-383">
          {offices.map((office, index) => <div key={index} className={`bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 card-hover transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} style={{
          transitionDelay: `${index * 150}ms`
        }} data-id="element-384">
              <div className="relative h-48" data-id="element-385">
                <img src={office.image} alt={`${office.city} Office`} className="w-full h-full object-cover" data-id="element-386" />
                {office.primary && <div className="absolute top-4 right-4" data-id="element-387">
                    <div className="bg-teal text-white text-xs font-medium px-3 py-1 rounded-full" data-id="element-388">
                      Headquarters
                    </div>
                  </div>}
              </div>
              <div className="p-6" data-id="element-389">
                <h3 className="text-xl font-bold mb-2 text-charcoal" data-id="element-390">
                  {office.city}, {office.country}
                </h3>
                <div className="flex items-start mb-4" data-id="element-391">
                  <MapPinIcon className="h-5 w-5 text-teal mr-2 mt-0.5 flex-shrink-0" data-id="element-392" />
                  <p className="text-charcoal/70" data-id="element-393">{office.address}</p>
                </div>
                <a href={`https://maps.google.com/?q=${office.city}+${office.address}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-teal font-medium hover:text-aqua transition-colors" data-id="element-394">
                  Get Directions
                  <svg className="ml-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-395">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-396" />
                  </svg>
                </a>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
const FaqSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  const toggleAccordion = index => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const faqs = [{
    question: 'How quickly can I get a response to my inquiry?',
    answer: 'We aim to respond to all inquiries within 24 hours during business days. For urgent matters, we recommend calling our support line directly.'
  }, {
    question: 'Can I schedule a demo of your training platform?',
    answer: "Yes, you can schedule a personalized demo with our team. Simply fill out the contact form and select 'Enterprise Solutions' as your reason for contact, or click on the 'Book a Demo' button in the Enterprise Solutions section."
  }, {
    question: 'Do you offer custom training solutions for enterprises?',
    answer: 'Absolutely! We provide tailored training solutions for enterprises of all sizes. This includes custom learning paths, branded academies, and integration with your existing systems. Contact our enterprise team to discuss your specific requirements.'
  }, {
    question: 'How can I get technical support for course access issues?',
    answer: "For technical support, please contact our support team through the form above by selecting 'Customer Support' as your reason, or email support@kivarro.com with details of the issue you're experiencing."
  }, {
    question: 'Do you have a partner program for training providers?',
    answer: 'Yes, we have a partner program for training providers, educational institutions, and consultants. Please reach out to our partnerships team at partners@kivarro.com for more information.'
  }];
  return <section ref={sectionRef} className="py-20 bg-gray-50" data-id="element-397">
      <div className="container mx-auto px-4 md:px-6" data-id="element-398">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-399">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal/10 mb-6" data-id="element-400">
            <MessageSquareIcon className="h-6 w-6 text-teal" data-id="element-401" />
          </div>
          <h2 className="text-4xl font-bold mb-6 text-charcoal" data-id="element-402">
            Frequently Asked <span className="gradient-text" data-id="element-403">Questions</span>
          </h2>
          <p className="text-xl text-charcoal/70" data-id="element-404">
            Find quick answers to common questions about contacting and working
            with us.
          </p>
        </div>
        <div className="max-w-3xl mx-auto" data-id="element-405">
          <div className="space-y-4" data-id="element-406">
            {faqs.map((faq, index) => <div key={index} className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`} style={{
            transitionDelay: `${index * 100}ms`
          }} data-id="element-407">
                <button onClick={() => toggleAccordion(index)} className="flex justify-between items-center w-full px-6 py-5 text-left" data-id="element-408">
                  <h3 className="text-lg font-semibold text-charcoal" data-id="element-409">
                    {faq.question}
                  </h3>
                  <ChevronDownIcon className={`h-5 w-5 text-charcoal/60 transition-transform ${activeIndex === index ? 'rotate-180' : ''}`} data-id="element-410" />
                </button>
                <div className={`px-6 overflow-hidden transition-all duration-300 ${activeIndex === index ? 'max-h-40 pb-6' : 'max-h-0'}`} data-id="element-411">
                  <p className="text-charcoal/70" data-id="element-412">{faq.answer}</p>
                </div>
              </div>)}
          </div>
          <div className="text-center mt-12" data-id="element-413">
            <p className="text-charcoal/70 mb-6" data-id="element-414">
              Still have questions? We're here to help.
            </p>
            <a href="#support-form" className="inline-flex items-center px-8 py-4 bg-teal text-white font-medium rounded-lg hover:bg-teal/90 transition-colors shadow-md" data-id="element-415">
              Contact Our Team
              <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-416">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-417" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>;
};